// Read the submitted agreement once per venue/supplier/PO. Never use current terms.
const financialId = value => {
  if (Array.isArray(value)) return financialId(value[0])
  if (value?._id && value._id !== value) return financialId(value._id)
  return typeof value === 'string' || typeof value?.toHexString === 'function'
    ? String(value)
    : ''
}
const financialSnapshotKey = snapshot => {
  const money = [
    'goodsSubtotalPennies',
    'minOrderValuePennies',
    'configuredDeliveryFeePennies',
    'appliedDeliveryFeePennies',
    'orderTotalPennies',
  ]
  if (
    snapshot?.version !== 1 ||
    !money.every(
      field => Number.isSafeInteger(snapshot[field]) && snapshot[field] >= 0,
    ) ||
    typeof snapshot.deliveryFeeChargeEnabled !== 'boolean' ||
    !Number.isFinite(new Date(snapshot.capturedAt).getTime()) ||
    snapshot.orderTotalPennies !==
      snapshot.goodsSubtotalPennies + snapshot.appliedDeliveryFeePennies
  )
    return null
  const expectedFee =
    snapshot.deliveryFeeChargeEnabled &&
    snapshot.minOrderValuePennies > 0 &&
    snapshot.goodsSubtotalPennies < snapshot.minOrderValuePennies
      ? snapshot.configuredDeliveryFeePennies
      : 0
  if (
    snapshot.capturedAt == null ||
    snapshot.appliedDeliveryFeePennies !== expectedFee
  )
    return null
  return JSON.stringify([
    snapshot.version,
    financialId(snapshot.venueSupplier),
    new Date(snapshot.capturedAt).getTime(),
    ...money.map(field => snapshot[field]),
    snapshot.deliveryFeeChargeEnabled,
  ])
}
export const summarizeOrderFinancials = (orders = [], context = {}) => {
  const byPo = new Map()
  let legacyLineCount = 0
  let hasConflict = false
  for (const order of orders) {
    const snapshot = order.orderFinancialSnapshot
    if (!snapshot) {
      legacyLineCount += 1
      continue
    }
    const venueId = financialId(
      order.venueId || order.venue || context.venueId || context.venue,
    )
    const supplierId = financialId(
      order.supplierId ||
        order.supplier ||
        context.supplierId ||
        context.supplier ||
        order.product?.supplier,
    )
    const po = String(
      order.purchaseOrderNumber || context.purchaseOrderNumber || '',
    )
    const signature = financialSnapshotKey(snapshot)
    if (!venueId || !supplierId || !po || !signature) {
      hasConflict = true
      continue
    }
    const key = JSON.stringify([venueId, supplierId, po])
    const previous = byPo.get(key)
    if (previous && previous.signature !== signature) {
      hasConflict = true
      continue
    }
    byPo.set(key, {
      key,
      venueId,
      supplierId,
      purchaseOrderNumber: po,
      orderFinancialSnapshot: snapshot,
      signature,
    })
  }
  const purchaseOrders = [...byPo.values()].map(
    ({
      key,
      venueId,
      supplierId,
      purchaseOrderNumber,
      orderFinancialSnapshot,
    }) => ({
      key,
      venueId,
      supplierId,
      purchaseOrderNumber,
      orderFinancialSnapshot,
    }),
  )
  const totals = {
    goodsSubtotalPennies: 0,
    appliedDeliveryFeePennies: 0,
    orderTotalPennies: 0,
  }
  for (const po of purchaseOrders) {
    for (const field of Object.keys(totals)) {
      totals[field] += po.orderFinancialSnapshot[field]
      if (!Number.isSafeInteger(totals[field])) hasConflict = true
    }
  }
  return {
    ...totals,
    purchaseOrders,
    legacyLineCount,
    hasConflict,
    hasFinancialSnapshots: purchaseOrders.length > 0,
  }
}

export const orderFinancialEmailSummary = (data = {}) => {
  const lines = [...(data.orders || [])]
  if (data.orderFinancialSnapshot)
    lines.push({ orderFinancialSnapshot: data.orderFinancialSnapshot })
  const summary = summarizeOrderFinancials(lines, data)
  if (summary.hasConflict) {
    const error = new Error(
      'Submitted order financial snapshots are inconsistent',
    )
    error.code = 'ORDER_FINANCIAL_SNAPSHOT_MISMATCH'
    throw error
  }
  return summary.hasFinancialSnapshots
    ? {
        goods: (summary.goodsSubtotalPennies / 100).toFixed(2),
        delivery: (summary.appliedDeliveryFeePennies / 100).toFixed(2),
        total: (summary.orderTotalPennies / 100).toFixed(2),
      }
    : null
}
